import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { CostCenterPriceModel } from 'src/app/models/archive/cost-center-price.model';
import { DevolutionDetailModel } from 'src/app/models/rental/devolution-detail.model';
import { DevolutionModel } from 'src/app/models/rental/devolution.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { DevolutionDetailCaptureTableModel } from 'src/app/models/tables/rental/devolution-detail-capture-table.model';
import { RentalStockModalModel } from 'src/app/models/tables/rental/rental-stock-modal.model';
import { DevolutionDetailService } from 'src/app/services/rental/devolution-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-devolution-detail',
  templateUrl: './devolution-detail.component.html',
  styleUrls: ['./devolution-detail.component.scss'],
  standalone: false
})
export class DevolutionDetailComponent implements OnInit, OnChanges, OnDestroy {


  @Input() devolutionModel: DevolutionModel = new DevolutionModel();
  @Input() disabledDetail: boolean = false;
  @Input() updateListDetail: boolean = false;
  @Output() devolutionDetailRepair = new EventEmitter<DevolutionDetailModel>();
  @Output() emitQuantityDetails = new EventEmitter<number>();
  @Output() openModalRepair = new EventEmitter<boolean>();


  listObservers: Array<Subscription> = new Array<Subscription>();

  ascendant: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  costCenterPriceContract: CostCenterPriceModel = new CostCenterPriceModel();
  detailData: PagedResponseModel<DevolutionDetailCaptureTableModel> = new PagedResponseModel<DevolutionDetailCaptureTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  devolutionDetailModel: DevolutionDetailModel = new DevolutionDetailModel();
  isFormView: boolean = false;
  isFormViewOpen: boolean = false;
  limit: number = 100;
  page: number = 1;
  tableActions: TableAction[] = [];

  constructor(private devolutionDetailService: DevolutionDetailService,
    private toastHandlerService: ToastHandlerService) {
  }

  async ngOnInit(): Promise<void> {
    await this.getColumns();
    this.tableActions = this.getDefaultActions();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnChanges(changes: SimpleChanges) {
    const updateListDetail = changes["updateListDetail"];
    if (updateListDetail && !updateListDetail.firstChange) {
      this.getDevolutionDetailsByDevolution();
    }
    const devolutionModel = changes["devolutionModel"];
    if (devolutionModel && !devolutionModel.firstChange) {
      this.getFormView(false);
      this.getDevolutionDetailsByDevolution();
      this.devolutionDetailModel = new DevolutionDetailModel();
      this.tableActions = this.disabledDetail ? this.getDefaultActionsDisabled() : this.getDefaultActions();
    }
  }

  ngOnDestroy() {
    this.listObservers.forEach(sub => sub.unsubscribe());
  }

  async createNewDetail() {
    this.isFormViewOpen = !this.isFormViewOpen;
    this.devolutionDetailModel = new DevolutionDetailModel();
  }

  async deleteDevolutionDetail(selectedIds: number[]) {
    if (selectedIds.length === 0) return;

    const result = await Swal.fire({
      title: 'Atención',
      text: "¿Realmente desea eliminar el detalle de la devolución?",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si',
      cancelButtonText: 'No'
    });
    if (result.isConfirmed) {
      try {
        if (this.devolutionModel.isPredevolution) {
          await Promise.all(selectedIds.map(id => {
            const detail = this.detailData.items.find(x => x.id === id)!;
            const stock = new RentalStockModalModel();
            stock.articleId = detail.articleId;
            stock.storeId = detail.storeId;
            stock.predevolutionQuantity = 0;
            return firstValueFrom(this.devolutionDetailService.savePredevolutionDetails(stock, this.devolutionModel.devolutionId));
          }));

        } else {
          await firstValueFrom(this.devolutionDetailService.deleteSomeDetails(selectedIds));
        }
        this.toastHandlerService.showToast(
          'success',
          'El detalle fue eliminado exitosamente.',
          undefined,
          undefined,
          5000
        );
        this.clearSelectionTrigger++;
        this.getUpdateListDetailByDelete();
      } catch (error) {
        this.getUpdateListDetailByDelete();
        console.error('Error al eliminar los detalles de la devolución.', error);
      }
    }
  }

  async getColumns() {
    try {
      this.detailHeader = await firstValueFrom(this.devolutionDetailService.getColumnsForCaptureTable());
    } catch (error) {
      console.error('Error searching columns for query.', error);
    }
  }

  getCostCenterPriceRegister(costCenterPrice: CostCenterPriceModel) {
    if (costCenterPrice.costCenterPriceId != 0) {
      this.getDevolutionDetailsByDevolution();
    }
  }

  getDefaultActions(): TableAction[] {
    if (this.devolutionModel.isPredevolution) {
      return [
        {
          label: 'Editar',
          icon: 'pi pi-pencil',
          action: 'edit'
        },
      ];
    } else {
      return [
        {
          label: 'Editar',
          icon: 'pi pi-pencil',
          action: 'edit'
        },
        {
          label: 'Reparación',
          icon: 'pi pi-wrench',
          action: 'repair',
        },
      ];

    }
  }

  getDefaultActionsDisabled(): TableAction[] {
    if (this.devolutionModel.isPredevolution) {
      return [];
    } else {
      return [
        {
          label: 'Reparación',
          icon: 'pi pi-wrench',
          action: 'repair',
        }
      ];

    }
  }

  async getDevolutionDetailsByDevolution() {
    try {
      this.detailData = await firstValueFrom(
        this.devolutionDetailService.getDevolutionDetailsForCaptureTable(
          this.page, this.limit, this.columnSort, this.ascendant, this.devolutionModel.devolutionId
        )
      );
      this.emitQuantityDetails.emit(this.detailData.totalItems);
    } catch (error) {
      console.error('Error searching details services.', error);
    }
  }

  async getDevolutionDetailById(devolutionDetailId: number) {
    try {
      this.devolutionDetailModel = await firstValueFrom(this.devolutionDetailService.getDevolutionDetailById(devolutionDetailId));
    } catch (error) {
      console.error('Error fetching devolution detail by ID.', error);
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  getUpdateListDetailByDelete() {
    this.devolutionDetailModel = new DevolutionDetailModel();
    this.clearSelectionTrigger++;
    this.getDevolutionDetailsByDevolution();
  }

  async onAction(event: { action: string, item?: DevolutionDetailCaptureTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit') {
      this.getFormView(true);
      await this.getDevolutionDetailById(event.item!.id);
    }
    if (event.action === 'repair') {
      const devolutionDetail = await firstValueFrom(this.devolutionDetailService.getDevolutionDetailById(event.item!.id));
      if (devolutionDetail.repairValue != 0 && !devolutionDetail.hasRepairs) {
        this.toastHandlerService.showToast('error', 'No puede agregar reparaciones a un detalle que tenga valor de reparación asignado manualmente.', 'Error');
      } else {
        this.devolutionDetailRepair.emit(devolutionDetail);
        this.openModalRepair.emit(false);
      }
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteDevolutionDetail(event.selectedItems);
      console.warn('Bulk delete action.', event.selectedItems);
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getDevolutionDetailsByDevolution();
  }

}
